import React from 'react'
import { cn } from '@/utils'
import { Inbox, BarChart3, DollarSign, Users, Unlock, TrendingUp, Calendar } from 'lucide-react'

export interface FundingMetricCardProps {
  icon: 'projects' | 'rounds' | 'amount' | 'investors' | 'unlock' | 'chart' | 'calendar'
  label: string
  value: React.ReactNode
  percentChange?: {
    value: string
    direction: 'up' | 'down'
    color: 'green' | 'red'
  }
  chart?: React.ReactNode
  className?: string
}

const iconMap = {
  projects: Inbox,
  rounds: BarChart3,
  amount: DollarSign,
  investors: Users,
  unlock: Unlock,
  chart: TrendingUp,
  calendar: Calendar,
}

export const FundingMetricCard: React.FC<FundingMetricCardProps> = ({
  icon,
  label,
  value,
  percentChange,
  chart,
  className,
}) => {
  const Icon = iconMap[icon]

  return (
    <div
      className={cn(
        'relative h-[124px] overflow-hidden bg-white dark:bg-[#121212]',
        'border-0 rounded-none',
        className
      )}
    >
      <div className="absolute left-[24px] top-[24px] flex h-[76px] flex-col items-start justify-between">
        <div className="flex items-start gap-[6px]">
          <div className="mt-px shrink-0 text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
            <Icon className="h-4 w-4" strokeWidth={1.6} />
          </div>
          <p className="max-w-[145px] whitespace-pre-wrap font-plus-jakarta text-[13px] font-medium leading-[18px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
            {label}
          </p>
        </div>

        <div className="flex items-center gap-[4px]">
          <p className="font-plus-jakarta text-[23px] font-medium leading-[32px] tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd]">
            {value}
          </p>
          {percentChange && (
            <div
              className={cn(
                'flex items-center justify-center gap-[4px] rounded-[16px] pb-[4px] pl-[6px] pr-[8px] pt-[3px]',
                'bg-[rgba(242,241,246,0.64)] dark:bg-[rgba(45,41,35,0.24)]',
                'shadow-[inset_1px_1px_4px_0px_rgba(217,214,227,0.4)] dark:shadow-[inset_1px_1px_4px_0px_rgba(0,0,0,0.4)]'
              )}
            >
              <div className="size-[12px] shrink-0">
                {percentChange.direction === 'up' ? (
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M9 8L3 2M3 2H8M3 2V7"
                      stroke={percentChange.color === 'green' ? '#2ea66b' : '#ff540c'}
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ) : (
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M9 4L3 10M3 10H8M3 10V5"
                      stroke={percentChange.color === 'green' ? '#2ea66b' : '#ff540c'}
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </div>
              <p
                className={cn(
                  'font-plus-jakarta text-[12px] font-semibold leading-[16px]',
                  percentChange.color === 'green' ? 'text-[#2ea66b]' : 'text-[#ff540c]'
                )}
              >
                {percentChange.value}
              </p>
            </div>
          )}
        </div>
      </div>

      {chart && (
        <div className="pointer-events-none absolute right-0 top-0 h-[124px] w-[185px] overflow-hidden">
          {chart}
        </div>
      )}
    </div>
  )
}

export default FundingMetricCard
